import moment from "moment";
import { http } from "@/http";
import { useToast } from "vue-toastification";
import i18n from "@/i18n";
import _ from "lodash";

// mutation types
const SET_PENDING = "SET_PENDING";
const SET_TIMESTAMP_INTERVAL = "SET_TIMESTAMP_INTERVAL";
const SET_TIMESTAMP = "SET_TIMESTAMP";
const SET_TIME_SHEETS_COMPACT = "SET_TIME_SHEETS_COMPACT";
const UPDATE_OFFSET = "UPDATE_OFFSET";
const DELETE_OFFSET = "DELETE_OFFSET";

const SET_TIME_SHEET_IDS = "SET_TIME_SHEET_IDS";
const SET_TIME_SHEETS = "SET_TIME_SHEETS";
const UPDATE_TIME_SHEET = "UPDATE_TIME_SHEET";

function formatDateTime(dateTime) {
  let minutes = dateTime.getMinutes();
  return `${dateTime.getHours()}:${minutes < 10 ? "0" : ""}${minutes}`;
}

const delayCounter = (targetTime) => (partialSum, delay) => {
  if (delay.pausedAt <= targetTime) {
    partialSum += delay.durationMillis;
  }
  return partialSum;
};

// initial state
const state = () => ({
  pending: false,
  timestamp: new Date(),
  timestampInterval: null,
  all: [],
  timeSheetIds: [],
  timeSheets: [],
});

// getters
const getters = {
  all(state) {
    return state.all;
  },
  getTimestamp(state) {
    return state.timestamp;
  },
  pending(state) {
    return state.pending;
  },
  timeSheets(state) {
    return state.timeSheets;
  },
  flightById: (state) => (id) => {
    for (const timeSheet of state.timeSheets) {
      for (const flight of timeSheet.flights) {
        if (flight.id == id) {
          return flight;
        }
      }
    }
    return null;
  },
};

// actions
const actions = {
  clearData({ commit }) {
    commit(SET_TIME_SHEETS_COMPACT, []);
  },
  setTimestampInterval({ commit, state }) {
    const { timestamp } = { ...state };
    const newInterval = setInterval(() => {
      commit(
        SET_TIMESTAMP,
        new Date(timestamp.setSeconds(timestamp.getSeconds() + 60))
      );
    }, 60000);
    commit(SET_TIMESTAMP_INTERVAL, newInterval);
  },
  unsetTimestampInterval({ commit }) {
    const { timestampInterval } = { ...state };
    clearInterval(timestampInterval);
    commit(SET_TIMESTAMP_INTERVAL, null);
  },
  async setTimeSheetsCompact({ commit, rootState }) {
    commit(SET_PENDING, true);
    const response = await http
      .get(`/api/v1/time-sheets/compact`, {
        // TODO: when the api gets secured by a jwt token, this has to be removed
        params: {
          clubNumber:
            rootState.permissions?.permissionTokenPayload?.clubNumber ?? "",
          clubCountry:
            rootState.permissions?.permissionTokenPayload?.clubCountry ?? "",
        },
      })
      .catch(() =>
        useToast().error(i18n.global.t("error.loadingTeeSheetList"))
      );
    commit(SET_TIME_SHEETS_COMPACT, response.data.data);
    commit(SET_PENDING, false);
  },
  updateTimeSheet: async ({ commit, state }, payload) => {
    commit(SET_PENDING, true);
    const { timeSheet, skipDelays, skipOvertakes } = payload;
    timeSheet.date = new Date(timeSheet.date);

    const storeTimeSheet = state.timeSheets.find((tS) => tS.id == timeSheet.id);

    if (timeSheet.flights != null) {
      if (timeSheet.offsets == undefined) {
        timeSheet.offsets = _.cloneDeep(storeTimeSheet?.offsets ?? []);
      }
      if (timeSheet.flightOvertakes == undefined) {
        timeSheet.flightOvertakes = _.cloneDeep(
          storeTimeSheet?.flightOvertakes ?? []
        );
      }
      if (timeSheet.delays == undefined) {
        timeSheet.delays = _.cloneDeep(storeTimeSheet?.delays ?? []);
      }
      if (timeSheet.refereeDecisions == undefined) {
        timeSheet.refereeDecisions = _.cloneDeep(
          storeTimeSheet?.refereeDecisions ?? []
        );
      }

      // TODO: sort holes by target time on server!
      timeSheet.flights.forEach((flight) => {
        // sort holes by target time
        flight.holes.sort(function (o1, o2) {
          const t1 = new Date(o1.targetTime).getTime();
          const t2 = new Date(o2.targetTime).getTime();
          return t1 - t2;
        });
      });

      // adjust hole target times and flights order by overtakes
      if (!skipOvertakes) {
        for (const flightOvertake of timeSheet.flightOvertakes) {
          // get indices of source- and target flight
          const targetFlightIndex = timeSheet.flights.findIndex(
            (flight) => flight.id == flightOvertake.targetFlightId
          );
          const sourceFlightIndex = timeSheet.flights.findIndex(
            (flight) => flight.id == flightOvertake.sourceFlightId
          );

          // create temporary copy of source- and target flight
          const targetFlight = timeSheet.flights[targetFlightIndex];
          const sourceFlight = timeSheet.flights[sourceFlightIndex];

          // create temporary hole arrays of new source- and target flight holes
          if (targetFlight && sourceFlight) {
            const newSourceFlightHoles = sourceFlight.holes
              .slice(0, flightOvertake.overtakingHoleNumber - 1)
              .concat(
                targetFlight.holes.slice(
                  flightOvertake.overtakingHoleNumber - 1
                )
              );
            const newTargetFlightHoles = targetFlight.holes
              .slice(0, flightOvertake.overtakingHoleNumber - 1)
              .concat(
                sourceFlight.holes.slice(
                  flightOvertake.overtakingHoleNumber - 1
                )
              );

            // set updated hole values and swap flights
            sourceFlight.holes = newSourceFlightHoles;
            targetFlight.holes = newTargetFlightHoles;
            timeSheet.flights[targetFlightIndex] = sourceFlight;
            timeSheet.flights[sourceFlightIndex] = targetFlight;
          }
        }
      }

      timeSheet.delays.forEach((delay) => {
        delay.pausedAt = new Date(delay.pausedAt);
        delay.pausedAtFormatted = formatDateTime(delay.pausedAt);
        delay.continuedAt = new Date(delay.continuedAt);
        delay.continuedAtFormatted = formatDateTime(delay.continuedAt);
      });

      timeSheet.flights.forEach((flight) => {
        flight.timeSheetId = timeSheet.id;
        flight.timeSheetType = timeSheet.type;
        flight.time = new Date(flight.time);

        const flightDelay = timeSheet.delays.reduce(
          delayCounter(flight.time),
          0
        );

        flight.time.setMilliseconds(
          flight.time.getMilliseconds() + flightDelay
        );

        // set time in different formats to reduce effort on re-render
        flight.timeMillis = flight.time.getTime();
        flight.timeFormatted = formatDateTime(flight.time);

        let hasDelayEffected = false;
        flight.holes.forEach((hole, holeIndex) => {
          hole.targetTime = new Date(hole.targetTime);

          if (!skipDelays) {
            const holeDelay = timeSheet.delays.reduce(
              delayCounter(hole.targetTime),
              0
            );

            if (hasDelayEffected == false && holeDelay > 0) {
              hole.isFirstAffectedByDelay = true;
              hasDelayEffected = true;
            }

            hole.targetTime.setMilliseconds(
              hole.targetTime.getMilliseconds() + holeDelay
            );
          }

          // set time in different formats to reduce effort on re-render
          hole.targetTimeMillis = hole.targetTime.getTime();
          hole.targetTimeFormatted = formatDateTime(hole.targetTime);
          hole.prevHoleTargetTimeMillis =
            holeIndex - 1 >= 0
              ? flight.holes[holeIndex - 1].targetTimeMillis
              : flight.timeMillis;

          // map offsets from array to hole
          hole.offset = timeSheet.offsets?.find(
            (offset) =>
              offset.flightId == flight.id && offset.holeNumber == hole.number
          );

          hole.refereeDecisions = timeSheet.refereeDecisions?.filter(
            (ruleDecision) =>
              ruleDecision.flightId == flight.id &&
              ruleDecision.holeNumber == hole.number
          );

          // detect additional hole information on flight overtake, if available
          hole.isOvertakingHole = false;
          const flightOvertake = timeSheet.flightOvertakes.find(
            (f) =>
              flight.id == f.sourceFlightId &&
              f.overtakingHoleNumber == hole.number
          );
          if (flightOvertake) {
            hole.isOvertakingHole = true;
            let overtakenFlight = timeSheet?.flights?.find(
              (flight) => flight.id == flightOvertake.targetFlightId
            );
            if (overtakenFlight != null) {
              hole.overtakenFlightNumber = overtakenFlight.number;
              hole.flightOvertakeUserInitials = flightOvertake.userInitials;
            }
          }
        });
      });
    }
    commit(UPDATE_TIME_SHEET, timeSheet);
    commit(SET_PENDING, false);
  },
  updateOffset: ({ commit }, payload) => {
    commit(UPDATE_OFFSET, payload);
  },
  deleteOffset: ({ commit }, offset) => {
    offset.flightTimeMillis = moment(offset.flightTime).valueOf();
    commit(DELETE_OFFSET, offset);
  },
  updateRefereeDecision: ({ dispatch, state, commit }, payload) => {
    commit(SET_PENDING, true);
    const timeSheetIndex = state.timeSheets?.findIndex(
      (tS) => tS.id == payload.timeSheetId
    );
    if (timeSheetIndex >= 0) {
      const timeSheet = _.cloneDeep(state.timeSheets[timeSheetIndex]);
      const refereeDecisionIndex = timeSheet.refereeDecisions?.findIndex(
        (rD) => rD.id == payload.id
      );
      if (refereeDecisionIndex != -1) {
        timeSheet.refereeDecisions[refereeDecisionIndex] = payload;
      } else {
        timeSheet.refereeDecisions.push(payload);
      }
      dispatch("updateTimeSheet", {
        timeSheet,
        skipDelays: true,
        skipOvertakes: true,
      });
    }
    commit(SET_PENDING, false);
  },
  removeRefereeDecision: ({ dispatch, state, commit }, payload) => {
    commit(SET_PENDING, true);
    const timeSheetIndex = state.timeSheets?.findIndex(
      (tS) => tS.id == payload.timeSheetId
    );
    if (timeSheetIndex >= 0) {
      const timeSheet = _.cloneDeep(state.timeSheets[timeSheetIndex]);
      timeSheet.refereeDecisions = timeSheet.refereeDecisions.filter(
        (rD) => rD.id != payload.id
      );
      dispatch("updateTimeSheet", {
        timeSheet,
        skipDelays: true,
        skipOvertakes: true,
      });
    }
    commit(SET_PENDING, false);
  },
  setTimeSheetIds: async ({ commit, dispatch }, payload) => {
    commit(SET_PENDING, true);
    const timeSheetRequests = payload.map(async (timeSheetId) => {
      const response = await http
        .get(`/api/v1/time-sheets/${timeSheetId}`)
        .catch(() => useToast().error(i18n.global.t("error.loadingTeeSheet")));
      return response.data.data;
    });
    const timeSheets = await Promise.all(timeSheetRequests);
    timeSheets.forEach((timeSheet) => {
      dispatch("updateTimeSheet", {
        timeSheet,
      });
    });
    commit(SET_TIME_SHEET_IDS, payload);
    commit(SET_PENDING, false);
  },
  reloadTimeSheet: async ({ commit, dispatch }, timeSheetId) => {
    commit(SET_PENDING, true);
    const response = await http
      .get(`/api/v1/time-sheets/${timeSheetId}`)
      .catch(() => useToast().error(i18n.global.t("error.loadingTeeSheet")));
    const timeSheet = response.data.data;
    dispatch("updateTimeSheet", {
      timeSheet,
    });
    commit(SET_PENDING, false);
  },
};

// mutations
const mutations = {
  [SET_TIME_SHEET_IDS](state, payload) {
    state.timeSheets = state.timeSheets.filter((tS) => payload.includes(tS.id));
    state.timeSheetIds = payload;
  },
  [UPDATE_TIME_SHEET](state, payload) {
    const timeSheetIndex = state.timeSheets.findIndex(
      (tS) => tS.id == payload.id
    );
    if (timeSheetIndex >= 0) {
      state.timeSheets[timeSheetIndex] = { ...payload };
    } else {
      state.timeSheets.push(payload);
    }
  },
  [SET_TIME_SHEETS](state, payload) {
    state.timeSheets = payload;
  },
  [SET_PENDING](state, pending) {
    state.pending = pending;
  },
  [SET_TIMESTAMP](state, timestamp) {
    state.timestamp = timestamp.getTime();
  },
  [SET_TIMESTAMP_INTERVAL](state, timestampInterval) {
    state.timestampInterval = timestampInterval;
  },
  [SET_TIME_SHEETS_COMPACT](state, timeSheetsCompact) {
    state.all = timeSheetsCompact;
  },
  [UPDATE_OFFSET](state, payload) {
    const timeSheetIndex = state.timeSheets?.findIndex(
      (tS) => tS.id == payload.timeSheetId
    );
    if (timeSheetIndex >= 0) {
      const offsetIndex = state.timeSheets[timeSheetIndex].offsets?.findIndex(
        (offset) =>
          offset.flightId == payload.flightId &&
          offset.holeNumber == payload.holeNumber
      );
      if (offsetIndex >= 0) {
        state.timeSheets[timeSheetIndex].offsets[offsetIndex].value =
          payload.value;
      } else {
        state.timeSheets[timeSheetIndex].offsets.push(payload);
        state.timeSheets[timeSheetIndex].flights.forEach((flight) => {
          flight.holes.forEach((hole) => {
            hole.offset = state.timeSheets[timeSheetIndex].offsets?.find(
              (offset) =>
                offset.flightId == flight.id && offset.holeNumber == hole.number
            );
          });
        });
      }
    }
  },
  [DELETE_OFFSET](state, payload) {
    const timeSheetIndex = state.timeSheets?.findIndex(
      (tS) => tS.id == payload.timeSheetId
    );
    if (timeSheetIndex >= 0) {
      state.timeSheets[timeSheetIndex].offsets = state.timeSheets[
        timeSheetIndex
      ].offsets.filter(
        (offset) =>
          !(
            offset.flightId == payload.flightId &&
            offset.holeNumber == payload.holeNumber
          )
      );
      state.timeSheets[timeSheetIndex].flights.forEach((flight) => {
        flight.holes.forEach((hole) => {
          hole.offset = state.timeSheets[timeSheetIndex].offsets?.find(
            (offset) =>
              offset.flightId == flight.id && offset.holeNumber == hole.number
          );
        });
      });
    }
  },
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};
